import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'sonner';

import getWebchatUrl from '../../../../utils/getWebchatUrl';
import { ChevronsUpDown, Loader2, MapPin } from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { CustomButton } from '@/components/ui/custom/CustomButton';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import usePersistentOpen from '@/hooks/usePersistentOpen';
import { useIsMobile } from '@/hooks/use-mobile';
import { ActionSheet } from '@/components/mobile';
import { cn } from '@/lib/utils';
import { DetailsSectionHeader } from './DetailsSection';

const LocationTransferMenu = (props) => {
	const webchatApiUrl = useMemo(
		() => getWebchatUrl(window.location.href),
		[]
	);

	const businessGroup = useSelector((state) => state.businessGroup);
	const webchatLocations = useSelector((state) => state.webchatLocations);
	const auth = useSelector((state) => state.auth);

	const [_location, setLocation] = useState(null);
	const [loading, setLoading] = useState(false);
	const [businessName, setBusinessName] = useState(null);
	const [businessAddress, setBusinessAddress] = useState(null);
	const [isOpen, setIsOpen] = usePersistentOpen(
		'conversation-details:transfer',
	);
	const isMobile = useIsMobile();
	const [pickerOpen, setPickerOpen] = useState(false);

	const handleSelectLocation = useCallback(
		(selectedLocation) => {
			const businesses =
				webchatLocations.length > businessGroup.length
					? webchatLocations
					: businessGroup;

			const businessesWithoutCurrentLocation = businesses.filter(
				(business) => business._id !== auth._id
			);
			const activeBusiness = businessesWithoutCurrentLocation.find(
				(business) => business._id === selectedLocation
			);

			setLocation(selectedLocation);
			setBusinessName(activeBusiness?.name ?? null);
			setBusinessAddress(activeBusiness?.address ?? null);
		},
		[webchatLocations, businessGroup, auth._id]
	);

	const transferLocation = useCallback(async () => {
		try {
			const { activeConversation } = props;
			const _customerConversation = activeConversation._id;

			setLoading(true);

			const newDataRes = await axios(
				`${webchatApiUrl}/api/team/transfer-conversation`,
				{
					method: 'post',
					data: {
						_customerConversation,
						_newBusiness: _location,
					},
					withCredentials: true,
				}
			);

			setLoading(false);

			if (newDataRes.data.error) {
				toast.error(
					newDataRes.data.message ||
						'Could not transfer conversation.'
				);
				return;
			}

			toast.success(
				businessName
					? `Conversation transferred to ${businessName}`
					: 'Conversation transferred'
			);
		} catch (err) {
			setLoading(false);
			console.log(err);
			toast.error('Could not transfer conversation.');
		}
	}, [_location, businessName, props.activeConversation, webchatApiUrl]);

	const renderLocationSelector = () => {
		const initialValue = 'Select Location';

		if (loading) {
			return (
				<div className="flex items-center justify-center w-full">
					<Loader2 className="animate-spin size-8 text-primary" />
				</div>
			);
		}

		const businesses =
			webchatLocations.length > businessGroup.length
				? webchatLocations
				: businessGroup;

		const businessesWithoutCurrentLocation = businesses.filter(
			(business) => business._id !== auth._id
		);

		// Phones: a picker row that opens an action sheet (addresses wrap
		// instead of running off a narrow select), and a full-width Transfer.
		if (isMobile) {
			return (
				<div className="w-full space-y-2">
					<button
						type="button"
						onClick={() => setPickerOpen(true)}
						aria-haspopup="dialog"
						className="flex min-h-12 w-full items-center gap-3 rounded-lg border border-input bg-background px-3 py-2 text-left transition-colors active:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:bg-zinc-900"
					>
						<MapPin className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
						<span className="min-w-0 flex-1">
							<span
								className={cn(
									'block truncate text-base',
									!_location && 'text-muted-foreground',
								)}
							>
								{_location ? businessName : initialValue}
							</span>
							{_location && businessAddress && (
								<span className="block truncate text-sm text-muted-foreground">
									{businessAddress}
								</span>
							)}
						</span>
						<ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
					</button>
					<CustomButton
						className="min-h-11 w-full text-base"
						onClick={transferLocation}
						disabled={!_location || _location === initialValue}
						variant="outline"
					>
						Transfer
					</CustomButton>
					<ActionSheet
						open={pickerOpen}
						onOpenChange={setPickerOpen}
						title="Transfer to"
						items={businessesWithoutCurrentLocation.map((business) => ({
							id: business._id,
							label: business.name,
							description: business.address,
							checked: business._id === _location,
							onSelect: () => handleSelectLocation(business._id),
						}))}
					/>
				</div>
			);
		}

		const menuItems = businessesWithoutCurrentLocation.map((business) => (
			<SelectItem value={business._id} key={business._id}>
				<div className="flex flex-col space-y-1">
					<p className="text-sm text-nowrap truncate">
						{business.name}
					</p>
					<p className="text-muted-foreground text-sm text-nowrap">
						{business.address}
					</p>
				</div>
			</SelectItem>
		));

		return (
			<div
				className="space-y-1.5"
				style={{
					width: 'inherit',
				}}
			>
				<Tooltip delayDuration={0}>
					<Select
						onValueChange={handleSelectLocation}
						value={_location || initialValue}
					>
						<TooltipTrigger
							style={{
								width: 'inherit',
							}}
						>
							<SelectTrigger>
								<SelectValue
									style={{
										width: 'inherit',
									}}
									className="overflow-hidden"
								>
									{_location ? (
										<div className="flex flex-col items-start m-2 text-wrap break-words">
											<p className="text-left">
												{businessName}
											</p>
										</div>
									) : (
										<div className="flex flex-col items-start m-2">
											{initialValue}
										</div>
									)}
								</SelectValue>
							</SelectTrigger>
						</TooltipTrigger>
						{businessAddress && (
							<TooltipContent align="start">
								{businessAddress}
							</TooltipContent>
						)}
						<SelectContent>{menuItems}</SelectContent>
					</Select>
				</Tooltip>
				<div>
					<CustomButton
						size="sm"
						className="w-full"
						onClick={transferLocation}
						disabled={!_location || _location === initialValue}
						variant="outline"
					>
						Transfer
					</CustomButton>
				</div>
			</div>
		);
	};

	if (businessGroup.length < 2 && webchatLocations.length < 2) {
		return null;
	}

	return (
		<div className="border-b border-border/60">
			<Collapsible open={isOpen} onOpenChange={setIsOpen}>
				<DetailsSectionHeader title="Transfer conversation" />

				<CollapsibleContent>
					<div className="px-4 pb-3 flex flex-col items-start space-y-1.5 w-full">
						<p className="text-xs text-muted-foreground max-md:text-sm">
							Choose a location to copy the conversation to
						</p>
						{renderLocationSelector()}
					</div>
				</CollapsibleContent>
			</Collapsible>
		</div>
	);
};

export default LocationTransferMenu;
